import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin, Phone, Navigation, MessageSquare, Search, Heart, Crosshair,
  CalendarClock, Wrench, ChevronRight,
} from "lucide-react";
import { STORES, CITIES, CITY_CENTERS } from "../data/stores";
import { useTestRide } from "../components/TestRideContext";

const SERVICE_TINT = {
  Rent:    { bg: "#EAF1FE", fg: "#1E6FE5" },
  Buy:     { bg: "#E8FAF2", fg: "#15B47E" },
  Service: { bg: "#F1ECFE", fg: "#7C3AED" },
};

export default function Stores() {
  const [city, setCity] = useState("Bengaluru");
  const [query, setQuery] = useState("");
  const [favs, setFavs] = useState({});
  const { openModal } = useTestRide();

  const filtered = useMemo(() => {
    return STORES.filter((s) => {
      const cityOk = city === "All Cities" || s.city === city;
      const q = query.trim().toLowerCase();
      const queryOk =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q);
      return cityOk && queryOk;
    });
  }, [city, query]);

  const center = CITY_CENTERS[city] || CITY_CENTERS["All Cities"];
  // Google Maps embed (no API key required)
  const mapSrc = `https://maps.google.com/maps?q=${center.lat},${center.lng}&t=&z=${center.zoom}&ie=UTF8&iwloc=&output=embed`;

  const toggleFav = (id) =>
    setFavs((f) => ({ ...f, [id]: !f[id] }));

  return (
    <div data-testid="stores-page" className="bg-white">
      {/* HERO + MAP (split layout matching reference) */}
      <section className="relative pt-12 lg:pt-16 border-b border-slate-200 overflow-hidden bg-gradient-to-b from-[#F0F6FF] to-white">
        <div className="absolute inset-0 helix-bg opacity-60" />
        <div className="container-x relative grid lg:grid-cols-2 gap-10 lg:gap-12 pb-12">
          {/* LEFT — title + search + first 4 store cards */}
          <div>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.98] tracking-tighter text-slate-900">
              Find your nearest
              <br />
              eBikeGo Store
              <span className="text-[#2A8BFF]">.</span>
            </h1>
            <p className="text-slate-600 text-base lg:text-lg mt-5 max-w-md">
              Book a test ride, check rental availability, schedule service or walk into your nearest high-street e-cycle store.
            </p>

            <div className="relative mt-8 max-w-xl">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                data-testid="store-search"
                placeholder="Enter city, locality or store name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-full pl-14 pr-14 py-4 text-slate-900 text-sm placeholder:text-slate-400 focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/15 focus:outline-none transition shadow-sm"
              />
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-slate-200 inline-flex items-center justify-center text-slate-500 hover:text-[#2A8BFF] hover:border-[#2A8BFF] transition"
                aria-label="Use my location"
                data-testid="locate-me"
              >
                <Crosshair size={16} />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mt-5" data-testid="city-filters">
              {CITIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCity(c)}
                  data-testid={`city-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-[11px] uppercase tracking-[0.18em] font-bold px-4 py-2.5 border rounded-full transition-all ${
                    city === c
                      ? "border-[#2A8BFF] bg-[#2A8BFF] text-white shadow-[0_6px_18px_rgba(42,139,255,0.22)]"
                      : "border-slate-200 hover:border-slate-400 text-slate-700 bg-white"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-8 space-y-4" data-testid="stores-list">
              {filtered.slice(0, 4).map((s, i) => (
                <StoreCard key={s.id} s={s} index={i} fav={!!favs[s.id]} onFav={() => toggleFav(s.id)} onBook={() => openModal({ city: s.city })} />
              ))}
              {filtered.length === 0 && (
                <p className="text-center text-slate-500 py-10">No stores match your search.</p>
              )}
              {filtered.length > 4 && (
                <a
                  href="#all-stores"
                  data-testid="view-more"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-[#1E6FE5] hover:text-[#2A8BFF]"
                >
                  View more stores in {city === "All Cities" ? "India" : city}{" "}
                  <ChevronRight size={16} />
                </a>
              )}
            </div>
          </div>

          {/* RIGHT — embedded map (sticky on desktop) */}
          <div className="relative">
            <div className="lg:sticky lg:top-28">
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_18px_60px_rgba(11,19,32,0.08)] bg-white">
                <iframe
                  key={city}
                  title={`eBikeGo stores in ${center.label}`}
                  src={mapSrc}
                  className="w-full h-[520px] lg:h-[680px] border-0 block"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
                {/* Floating helix pin overlays for vibe — purely decorative */}
                <DecorPins count={filtered.length} city={city} />

                {/* Bottom info bar */}
                <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur border border-slate-200 rounded-2xl px-4 py-3 flex items-center justify-between shadow-sm">
                  <p className="text-sm text-slate-700">
                    <span className="text-[#1E6FE5] font-bold">{filtered.length}</span> stores{" "}
                    {city !== "All Cities" ? `in ${city}` : "across India"}
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                    Live network
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACCESS STRIP — like reference */}
      <section className="container-x py-10 -mt-1">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {[
            { icon: <CalendarClock size={18} />, t: "Check Rental Availability", s: "See e-cycles available near you",   bg: "#E8FAF2", fg: "#15B47E", onClick: () => openModal() },
            { icon: <Wrench size={18} />,        t: "Schedule Service",          s: "Book a service appointment",        bg: "#F1ECFE", fg: "#7C3AED", href: "/service" },
            { icon: <MessageSquare size={18} />, t: "WhatsApp Store",            s: "Chat with store on WhatsApp",       bg: "#E8FAF2", fg: "#15B47E", href: "#" },
            { icon: <Phone size={18} />,         t: "Call Store",                s: "Speak to our experts",              bg: "#EAF1FE", fg: "#1E6FE5", href: "tel:+918655692201" },
          ].map((q, i) => {
            const inner = (
              <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-4 hover:border-[#2A8BFF]/40 hover:shadow-md transition group h-full">
                <span
                  className="w-12 h-12 rounded-2xl inline-flex items-center justify-center shrink-0"
                  style={{ background: q.bg, color: q.fg }}
                >
                  {q.icon}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-outfit font-bold text-slate-900 text-sm sm:text-base leading-tight">{q.t}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{q.s}</p>
                </div>
                <ChevronRight size={16} className="text-slate-400 group-hover:text-[#2A8BFF] transition" />
              </div>
            );
            return q.href ? (
              <a key={i} href={q.href} className="block" data-testid={`quick-${i}`}>
                {inner}
              </a>
            ) : (
              <button key={i} onClick={q.onClick} className="text-left" data-testid={`quick-${i}`}>
                {inner}
              </button>
            );
          })}
        </div>
      </section>

      {/* ALL STORES GRID */}
      <section id="all-stores" className="container-x pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="eyebrow">All stores</p>
            <h2 className="font-outfit text-3xl lg:text-4xl font-bold mt-3 text-slate-900">
              {city === "All Cities" ? "Across India" : `Stores in ${city}`}
            </h2>
          </div>
          <p className="text-sm text-slate-500 hidden sm:block">
            {filtered.length} location{filtered.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((s, i) => (
            <StoreCard key={s.id} s={s} index={i} fav={!!favs[s.id]} onFav={() => toggleFav(s.id)} onBook={() => openModal({ city: s.city })} />
          ))}
        </div>
      </section>

      {/* NETWORK STATS — match reference look */}
      <section className="bg-[#F6F8FB] border-t border-slate-200 py-16">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-4">
          {[
            { v: "65+",     l: "Stores across India",     sub: "Growing every month",     icon: <MapPin size={20} />,        bg: "#EAF1FE", fg: "#1E6FE5" },
            { v: "10,000+", l: "Test rides every month",  sub: "Experience before you buy", icon: <Crosshair size={20} />,   bg: "#E8FAF2", fg: "#15B47E" },
            { v: "95+",     l: "Service centres",         sub: "Expert care, guaranteed",   icon: <Wrench size={20} />,      bg: "#F1ECFE", fg: "#7C3AED" },
            { v: "24/7",    l: "Support nearby",          sub: "We're always here",         icon: <Phone size={20} />,       bg: "#FFEEE3", fg: "#F0682E" },
          ].map((s) => (
            <div key={s.l} className="flex items-start gap-4">
              <span
                className="w-12 h-12 rounded-2xl inline-flex items-center justify-center shrink-0"
                style={{ background: s.bg, color: s.fg }}
              >
                {s.icon}
              </span>
              <div>
                <p className="font-outfit text-3xl lg:text-4xl font-bold text-slate-900 leading-none">{s.v}</p>
                <p className="text-sm font-bold text-slate-900 mt-2">{s.l}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ---------- Sub components ---------- */

function StoreCard({ s, index, fav, onFav, onBook }) {
  const isOpen = s.status === "open";
  return (
    <motion.article
      data-testid={`store-${s.id}`}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.04 }}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#2A8BFF]/40 hover:shadow-[0_18px_45px_rgba(11,19,32,0.06)] transition"
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image with Open / Closing badge */}
        <div className="relative w-full sm:w-40 h-40 sm:h-auto shrink-0 bg-slate-100">
          <img src={s.image} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
          <span
            className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full ${
              isOpen
                ? "bg-[#15B47E] text-white"
                : "bg-[#F0682E] text-white"
            }`}
          >
            {isOpen ? "Open" : "Closing soon"}
          </span>
        </div>

        {/* Details */}
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-outfit font-bold text-lg text-slate-900 leading-snug">{s.name}</h3>
            <button
              onClick={onFav}
              data-testid={`store-${s.id}-fav`}
              aria-label="Save store"
              className={`shrink-0 w-9 h-9 rounded-full border inline-flex items-center justify-center transition ${
                fav
                  ? "bg-[#FFEEEE] border-[#FCA5A5] text-[#E53935]"
                  : "border-slate-200 text-slate-400 hover:text-[#E53935] hover:border-[#FCA5A5]"
              }`}
            >
              <Heart size={15} fill={fav ? "#E53935" : "none"} />
            </button>
          </div>

          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            {s.address}
          </p>
          <p className="text-xs text-slate-600 mt-2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#15B47E]" />
            <span className="font-semibold">{s.hours}</span>
            <span className="text-slate-400">·</span>
            <span>{s.statusLabel}</span>
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {s.services.map((srv) => {
              const t = SERVICE_TINT[srv] || { bg: "#F6F8FB", fg: "#1F2A3A" };
              return (
                <span
                  key={srv}
                  className="text-[10px] uppercase tracking-[0.16em] font-bold px-2.5 py-1 rounded-full"
                  style={{ background: t.bg, color: t.fg }}
                >
                  {srv}
                </span>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <button
              onClick={onBook}
              data-testid={`store-${s.id}-book`}
              className="text-[12px] font-bold uppercase tracking-wider text-[#1E6FE5] border border-[#1E6FE5] rounded-full px-4 py-2 hover:bg-[#1E6FE5] hover:text-white transition"
            >
              Book Test Ride
            </button>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`store-${s.id}-directions`}
              className="text-[12px] font-bold uppercase tracking-wider text-slate-700 border border-slate-200 rounded-full px-4 py-2 hover:border-slate-400 transition inline-flex items-center gap-1.5"
            >
              <Navigation size={13} /> Directions
            </a>
            <a
              href={`tel:${s.phone}`}
              data-testid={`store-${s.id}-call`}
              aria-label="Call store"
              className="w-9 h-9 rounded-full bg-[#EAF1FE] text-[#1E6FE5] inline-flex items-center justify-center hover:bg-[#1E6FE5] hover:text-white transition"
            >
              <Phone size={14} />
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

/**
 * Decorative helix pins floated on top of the Google Maps iframe.
 * They are static visual flair — the real map is interactive underneath.
 */
function DecorPins({ count = 4, city = "Bengaluru" }) {
  // Spread positions vary per city to feel different
  const layouts = {
    "Bengaluru": [
      { l: "26%", t: "28%" }, { l: "52%", t: "48%" }, { l: "78%", t: "40%" }, { l: "44%", t: "70%" },
    ],
    "Mumbai":    [{ l: "28%", t: "30%" }, { l: "52%", t: "40%" }, { l: "70%", t: "58%" }],
    "Delhi":     [{ l: "30%", t: "32%" }, { l: "58%", t: "50%" }],
    "Pune":      [{ l: "32%", t: "35%" }, { l: "62%", t: "55%" }],
    "Hyderabad": [{ l: "30%", t: "38%" }, { l: "58%", t: "60%" }],
    "Chennai":   [{ l: "32%", t: "40%" }, { l: "62%", t: "58%" }],
    "All Cities":[
      { l: "32%", t: "26%" }, { l: "26%", t: "55%" }, { l: "62%", t: "58%" },
      { l: "58%", t: "72%" }, { l: "44%", t: "80%" }, { l: "50%", t: "44%" },
    ],
  };
  const pins = (layouts[city] || layouts["Bengaluru"]).slice(0, Math.max(2, Math.min(count, 6)));

  return (
    <div className="pointer-events-none absolute inset-0">
      {pins.map((p, i) => (
        <div
          key={i}
          className="absolute -translate-x-1/2 -translate-y-full drop-shadow-[0_6px_10px_rgba(0,0,0,0.25)]"
          style={{ left: p.l, top: p.t }}
        >
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-white border-2 border-white flex items-center justify-center overflow-hidden ring-1 ring-black/10">
              <img
                src="/ebikego-icon.png"
                alt="eBikeGo store"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 bg-white rotate-45 ring-1 ring-black/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function HelixMark() {
  return null; // no longer used — pins render the real eBikeGo Icon
}
